import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import api, { studiesApi, specialtiesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, FlaskConical, FileCheck, ShieldCheck, Banknote, Loader2 } from 'lucide-react';
import IRBPanel from '../components/IRBPanel';
import { SpecialtyMultiSelect } from '../components/SpecialtyMultiSelect';

export default function CreateStudy() {
  const { isCompanyRep, isCoordinator } = useAuth();
  const navigate = useNavigate();
  const isCoordinatorOnly = isCoordinator() && !isCompanyRep();
  const [loading, setLoading] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [companies, setCompanies] = useState([]); // Coordinator wizard only
  const [step, setStep] = useState(1); // 1: study info, 2: pick first request type
  const [studyData, setStudyData] = useState({
    title: '',
    description: '',
    therapeutic_area: '',
    company_account_id: '',
    specialty_ids: [],
  });
  const [firstRequestType, setFirstRequestType] = useState(isCoordinatorOnly ? 'FT' : null);
  const [irbExpanded, setIrbExpanded] = useState(false);

  useEffect(() => {
    if (!isCompanyRep() && !isCoordinatorOnly) {
      navigate('/dashboard');
      return;
    }
    fetchSpecialties();
    if (isCoordinatorOnly) fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSpecialties = async () => {
    try {
      const res = await specialtiesApi.getAll();
      setSpecialties(res.data);
    } catch (error) {
      toast.error('Failed to load specialties');
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/company-accounts');
      setCompanies(res.data || []);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const handleNext = () => {
    if (!studyData.title.trim()) {
      toast.error('Study title is required');
      return;
    }
    if (studyData.title.length > 250) {
      toast.error('Study title must be 250 characters or less');
      return;
    }
    // Specialties are optional for Coordinators (admin-level users can edit them later)
    if (!isCoordinatorOnly && studyData.specialty_ids.length === 0) {
      toast.error('Please select at least one specialty');
      return;
    }
    setStep(2);
  };

  const handleCreate = async () => {
    if (!firstRequestType) {
      toast.error('Please select a request type to start with');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: studyData.title.trim(),
        description: studyData.description,
        specialty_ids: studyData.specialty_ids,
      };
      if (isCoordinatorOnly) {
        if (studyData.company_account_id) payload.company_account_id = studyData.company_account_id;
      } else {
        payload.therapeutic_area = studyData.therapeutic_area;
      }
      const res = await studiesApi.create(payload);
      const studyId = res.data.id;
      toast.success('Study created!');
      // Navigate to new request form with study_id and type preset
      navigate(`/requests/new?study_id=${studyId}&type=${firstRequestType}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create study');
    } finally {
      setLoading(false);
    }
  };

  // Request type cards — availability depends on who is creating the study:
  //   Company Rep:   FEA / IRB / CTA active, FT disabled (Coordinator-only)
  //   Coordinator:   FT active, FEA / IRB / CTA disabled (Company-only)
  const typeCard = ({ type, icon: Icon, title, desc, badgeClass, activeClasses, iconActive, enabled, selected, onClick, testid, hint }) => (
    <button
      key={type}
      type="button"
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      className={`p-6 rounded-xl border-2 text-left transition-all ${
        !enabled
          ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
          : selected
            ? activeClasses
            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
      }`}
      data-testid={testid}
    >
      <Icon className={`w-8 h-8 mb-3 ${enabled && selected ? iconActive : 'text-slate-400'}`} />
      <h3 className="font-semibold text-slate-900 mb-1">{title}</h3>
      <p className="text-xs text-slate-500">{desc}</p>
      <div className="mt-3 flex items-center gap-2">
        <Badge className={enabled ? badgeClass : 'bg-slate-200 text-slate-500'}>{type}</Badge>
        {!enabled && <span className="text-[10px] text-slate-400 italic">{hint}</span>}
      </div>
    </button>
  );

  return (
    <DashboardLayout title="Create Study">
      <div className="max-w-2xl mx-auto" data-testid="create-study-page">
        <Button variant="ghost" onClick={() => step === 2 ? setStep(1) : navigate(isCoordinatorOnly ? '/requests/new' : '/studies')} className="mb-6">
          <ArrowLeft size={16} className="mr-2" />
          {step === 2 ? 'Back to Study Info' : 'Back to Studies'}
        </Button>

        {/* iter166g — Polished step indicator: bigger pills with brand colors,
            animated connecting line, and clearer typography. */}
        <div className="flex items-center gap-3 mb-8">
          <div className={`flex items-center gap-2.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
            step === 1
              ? 'bg-blue-50 text-blue-700 border-blue-200 ring-2 ring-blue-200/60'
              : 'bg-emerald-50 text-emerald-700 border-emerald-200'
          }`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              step === 1 ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'
            }`}>{step === 1 ? '1' : '\u2713'}</span>
            Study Info
          </div>
          <div className={`h-px flex-1 max-w-[80px] transition-colors ${step === 2 ? 'bg-blue-300' : 'bg-slate-200'}`} />
          <div className={`flex items-center gap-2.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
            step === 2
              ? 'bg-blue-50 text-blue-700 border-blue-200 ring-2 ring-blue-200/60'
              : 'bg-slate-50 text-slate-400 border-slate-200'
          }`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              step === 2 ? 'bg-blue-600 text-white' : 'bg-slate-300 text-white'
            }`}>2</span>
            First Request
          </div>
        </div>

        {step === 1 && (
          <Card className="shadow-md border-slate-200">
            <CardHeader className="bg-gradient-to-br from-slate-50 to-white border-b border-slate-200 rounded-t-lg">
              <CardTitle className="font-['Manrope'] text-2xl tracking-tight">Create New Study</CardTitle>
              <CardDescription className="mt-1.5">A study groups all related requests together — feasibility, agreements, amendments.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="title">Study Title *</Label>
                  <span
                    className={`text-xs ${studyData.title.length > 250 ? 'text-red-600 font-medium' : 'text-slate-400'}`}
                    data-testid="study-title-counter"
                  >
                    {studyData.title.length} / 250
                  </span>
                </div>
                <Input
                  id="title"
                  value={studyData.title}
                  onChange={(e) => setStudyData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Novel Immunotherapy for Lung Cancer"
                  maxLength={250}
                  data-testid="study-title-input"
                />
                <p className="text-xs text-slate-400">Maximum 250 characters.</p>
              </div>

              {/* iter147 — Description field removed from Create New Study (per user). */}

              {isCoordinatorOnly ? (
                <div className="space-y-2">
                  <Label>Company <span className="text-slate-400 text-sm">(optional — admin can assign later)</span></Label>
                  <Select
                    value={studyData.company_account_id}
                    onValueChange={(v) => setStudyData(prev => ({ ...prev, company_account_id: v }))}
                  >
                    <SelectTrigger data-testid="study-company-select"><SelectValue placeholder="Pick a Company (optional)..." /></SelectTrigger>
                    <SelectContent>
                      {companies.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!studyData.company_account_id && (
                    <p className="text-[11px] text-amber-600">⚠ No Company selected — an admin-level user can assign one later.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="area">Therapeutic Area</Label>
                  <Input
                    id="area"
                    value={studyData.therapeutic_area}
                    onChange={(e) => setStudyData(prev => ({ ...prev, therapeutic_area: e.target.value }))}
                    placeholder="e.g., Oncology, Cardiology"
                    data-testid="study-area-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>
                  Specialties {isCoordinatorOnly
                    ? <span className="text-slate-400 text-sm">(optional — admin can edit later)</span>
                    : <>* <span className="text-slate-400 text-sm">(select one or more)</span></>}
                </Label>
                <SpecialtyMultiSelect
                  specialties={specialties}
                  value={studyData.specialty_ids || []}
                  onChange={(ids) => setStudyData((prev) => ({ ...prev, specialty_ids: ids }))}
                  testidPrefix="study-spec"
                />
              </div>

              <div className="pt-4 border-t flex justify-end">
                <Button onClick={handleNext} className="bg-blue-900 hover:bg-blue-950" data-testid="study-next-btn">
                  Next: Choose First Request
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="shadow-md border-slate-200">
            <CardHeader className="bg-gradient-to-br from-slate-50 to-white border-b border-slate-200 rounded-t-lg">
              <CardTitle className="font-['Manrope'] text-2xl tracking-tight">What would you like to start with?</CardTitle>
              <CardDescription className="mt-1.5">
                Choose the first request type for &ldquo;<strong>{studyData.title}</strong>&rdquo;. You can add more requests later.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {typeCard({
                  type: 'FEA',
                  icon: FlaskConical,
                  title: 'Feasibility Study',
                  desc: 'Evaluate if your institution can support this research',
                  badgeClass: 'bg-teal-100 text-teal-700',
                  activeClasses: 'border-teal-500 bg-teal-50 ring-2 ring-teal-200',
                  iconActive: 'text-teal-600',
                  enabled: !isCoordinatorOnly,
                  selected: firstRequestType === 'FEA',
                  onClick: () => { setIrbExpanded(false); setFirstRequestType('FEA'); },
                  testid: 'select-fea-btn',
                  hint: 'Company accounts only',
                })}

                {/* IRB — read-only resource library (no submission). Toggles inline preview below. */}
                {typeCard({
                  type: 'IRB',
                  icon: ShieldCheck,
                  title: 'Institutional Review Board',
                  desc: 'View IRB guidance and download required templates',
                  badgeClass: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
                  activeClasses: 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-200',
                  iconActive: 'text-yellow-600',
                  enabled: !isCoordinatorOnly,
                  selected: irbExpanded,
                  onClick: () => setIrbExpanded((v) => !v),
                  testid: 'select-irb-btn',
                  hint: 'Company accounts only',
                })}

                {typeCard({
                  type: 'CTA',
                  icon: FileCheck,
                  title: 'Clinical Trial Agreement',
                  desc: 'Formal agreement for conducting a clinical trial',
                  badgeClass: 'bg-violet-100 text-violet-700',
                  activeClasses: 'border-violet-500 bg-violet-50 ring-2 ring-violet-200',
                  iconActive: 'text-violet-600',
                  enabled: !isCoordinatorOnly,
                  selected: firstRequestType === 'CTA',
                  onClick: () => { setIrbExpanded(false); setFirstRequestType('CTA'); },
                  testid: 'select-cta-btn',
                  hint: 'Company accounts only',
                })}

                {typeCard({
                  type: 'FT',
                  icon: Banknote,
                  title: 'Financial Transaction',
                  desc: 'Track a financial transaction tied to this study',
                  badgeClass: 'bg-emerald-100 text-emerald-700',
                  activeClasses: 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200',
                  iconActive: 'text-emerald-600',
                  enabled: isCoordinatorOnly,
                  selected: firstRequestType === 'FT',
                  onClick: () => { setIrbExpanded(false); setFirstRequestType('FT'); },
                  testid: 'select-ft-btn',
                  hint: 'Coordinators only',
                })}
              </div>

              {/* Inline IRB preview (read-only) */}
              {irbExpanded && <IRBPanel />}

              <div className="pt-4 border-t flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button
                  onClick={handleCreate}
                  disabled={!firstRequestType || loading}
                  className="bg-blue-900 hover:bg-blue-950"
                  data-testid="create-study-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                  ) : (
                    'Create Study & Continue'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
